import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiTitle,
  EuiSpacer,
  EuiDescriptionList,
  EuiPanel,
} from "@elastic/eui";
import { OpsanioApplianceData } from "..";
import { ApplianceLocationMap } from "./location";
import { OpsanioLocationValue } from "src/questionnaires";
import { useApi } from "src/api";
import { ApplianceUserCards } from "./users";
import { OpsanioApplianceFlyoutFeatures } from "./features";
import {
  AnketaResourcesEnum,
  KeycloakUser,
  useAnketaAuth,
} from "../../../auth";
import { AnketaResponse } from "../../../api/types";
import { useAnketaI18n } from "src/internationalization";

// Content key of the Montagestandort (cpe) multi-location address form. Its
// fact persists as a JSON-stringified array of sites. The parser collapses
// this to the single primary site for helm values; the dashboard shows them all.
const CPE_LOCATIONS_KEY = "cpe.location";

export const OpsanioApplianceFlyoutMetadata = ({
  appliance,
}: {
  appliance: OpsanioApplianceData;
}): JSX.Element => {
  const { Get } = useApi();
  const { keycloak } = useAnketaAuth();
  const { translate } = useAnketaI18n();

  const [genericAcc, setGenericAcc] = useState<KeycloakUser[]>([]);
  const [salesAcc, setSalesAcc] = useState<KeycloakUser[]>([]);
  const [technicalAcc, setTechnicalAcc] = useState<KeycloakUser[]>([]);

  const formatDate = useCallback((dateString: string) => {
    if (dateString.trim() === "") {
      return "-";
    }
    console.log(dateString);
    const date = new Date(dateString);
    return date.toLocaleString("de-DE", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, []);

  const details = [
    {
      title: translate("anketa.displayName", {
        defaultMessage: "Display Name",
      }),
      description: appliance.displayName || "-",
    },
    {
      title: translate("anketa.opsanio.appliance.flyout.metadata.createdBy", {
        defaultMessage: "Created by",
      }),
      description: appliance.createdBy || "-",
    },
    {
      title: translate("anketa.events.flyout.create.at", {
        defaultMessage: "Created at",
      }),
      description: formatDate(appliance.createdAt),
    },
    {
      title: translate("anketa.opsanio.appliance.flyout.metadata.updatedBy", {
        defaultMessage: "Last modified by",
      }),
      description: appliance.updatedBy || "-",
    },
    {
      title: translate("anketa.opsanio.appliance.flyout.metadata.updatedAt", {
        defaultMessage: "Modified at",
      }),
      description: formatDate(appliance.updatedAt),
    },
    {
      title: translate("anketa.opsanio.appliance.flyout.metadata.name", {
        defaultMessage: "Name (Metadata)",
      }),
      description: appliance.common?.metadata?.name || "-",
    },
    {
      title: translate("anketa.opsanio.appliance.flyout.metadata.start", {
        defaultMessage: "Start (Runtime)",
      }),
      description: formatDate(appliance.common?.general?.dates?.start) || "-",
    },
    {
      title: translate("anketa.opsanio.appliance.flyout.metadata.end", {
        defaultMessage: "End (Runtime)",
      }),
      description: formatDate(appliance.common?.general?.dates?.end) || "-",
    },
  ];

  // All sites entered in the Montagestandort form. Read from the raw fact so
  // the dashboard surfaces every location, not just the single primary one the
  // parser hands to helm values. Tolerates a parsed array or a JSON string;
  // falls back to the parsed primary site when the raw fact is unavailable.
  const allLocations = useMemo<OpsanioLocationValue[]>(() => {
    const raw = appliance.content?.[CPE_LOCATIONS_KEY]?.value;
    let locations: OpsanioLocationValue[] = [];
    if (Array.isArray(raw)) {
      locations = raw as OpsanioLocationValue[];
    } else if (typeof raw === "string" && raw.trim() !== "") {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          locations = parsed as OpsanioLocationValue[];
        } else if (parsed) {
          locations = [parsed as OpsanioLocationValue];
        }
      } catch {
        locations = [];
      }
    }
    if (locations.length === 0 && appliance.common?.location) {
      locations = [appliance.common.location];
    }
    // Primary site (Hauptstandort) first, the rest in entry order.
    return [...locations].sort(
      (a, b) => Number(!!b.isPrimary) - Number(!!a.isPrimary),
    );
  }, [appliance.content, appliance.common?.location]);

  const canFetch = useMemo<boolean>(() => {
    if (keycloak) {
      if (typeof keycloak.token === "string") {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }, [keycloak]);

  const fetchContacts = () => {
    if (canFetch) {
      Get<any>({
        path: `v2/${AnketaResourcesEnum.questionnaires}/${appliance.catalogId}/${appliance.id}/contacts`,
        responseType: "json",
        token: keycloak.token,
      })
        .then(
          (
            response: AnketaResponse<{
              account: Array<KeycloakUser>;
              salesContact: Array<KeycloakUser>;
              technicalContact: Array<KeycloakUser>;
            }>,
          ) => {
            const { success, data } = response;
            if (success) {
              setGenericAcc(data.account);
              setSalesAcc(data.salesContact);
              setTechnicalAcc(data.technicalContact);
            }
          },
        )
        .catch((err) => {
          console.error(err);
        });
    }
  };

  useEffect(() => {
    fetchContacts();
  }, [canFetch]);

  return (
    <EuiPanel paddingSize="m" hasShadow={false} color="subdued">
      <EuiFlexGroup direction="column" gutterSize="m">
        <EuiFlexItem>
          <EuiTitle size="s">
            <h3>
              {translate("anketa.opsanio.appliance.flyout.metadata.title", {
                defaultMessage: "Appliance Details",
              })}
            </h3>
          </EuiTitle>
          <EuiSpacer size="s" />
          <EuiDescriptionList
            type="column"
            compressed
            listItems={details}
            textStyle="reverse"
          />
        </EuiFlexItem>

        {/* Which capabilities this appliance actually runs. Read from the
            questionnaire facts, because until now the AI/ML switch - the one
            that deploys a paid model gateway - was visible only inside the
            questionnaire's internal-only Features section. */}
        <EuiFlexItem>
          <OpsanioApplianceFlyoutFeatures appliance={appliance} />
        </EuiFlexItem>

        <EuiSpacer size="m" />
        {allLocations.length > 0 && (
          <EuiFlexItem>
            <EuiTitle size="xs">
              <h4>
                {allLocations.length > 1
                  ? translate(
                      "anketa.opsanio.appliance.flyout.metadata.locations",
                      { defaultMessage: "Locations" },
                    )
                  : translate("anketa.events.flyout.location.short", {
                      defaultMessage: "Location",
                    })}
              </h4>
            </EuiTitle>
            <EuiSpacer size="s" />
            <EuiFlexGroup direction="column" gutterSize="m">
              {allLocations.map((loc, i) => (
                <EuiFlexItem key={`${loc.displayName}-${loc.lat}-${loc.lng}-${i}`}>
                  <ApplianceLocationMap location={loc} />
                </EuiFlexItem>
              ))}
            </EuiFlexGroup>
          </EuiFlexItem>
        )}

        <EuiFlexItem>
          <ApplianceUserCards
            groups={[
              { type: "sales", users: salesAcc },
              { type: "technical", users: technicalAcc },
              { type: "customer", users: genericAcc },
            ]}
          />
        </EuiFlexItem>
      </EuiFlexGroup>
    </EuiPanel>
  );
};
